import { useState, useEffect } from 'react'
import Modal from 'aldoo-ra/Modal'
import ContentEditor from 'aldoo-ra/CMS/content-editor'
import SlotDataEditor from 'aldoo-ra/CMSModels/AldooLayoutSlot/editors/slot-data-editor'
import { showNotification } from 'aldoo-ra/Notification'
import { SaveCMSContent, GetCMSContent } from 'aldoo-ra/CMS/api'
import TypeResolver from 'aldoo-ra/CMS/type-resolver'
import { getPreSaveFormatter } from 'aldoo-ra/CMS/content-pre-save-formatter'
import ComponentEditorRegistry from './component-editors'

const AldooLayoutSlotDialog = ({ isOpen, onClose, slot, onSave: onSaveCallback }) => {
  const { getContentType } = TypeResolver()
  const AldooLayoutSlotType = getContentType('AldooLayoutSlot')

  const [selectedInstance, setSelectedInstance] = useState(null)
  const [customEditor, setCustomEditor] = useState(null)
  const [headerButtons, setHeaderButtons] = useState(null)
  const [useDataEditor, setUseDataEditor] = useState(false)

  useEffect(() => {
    if (slot) {
      // Editing existing slot - flatten content fields for ContentEditor
      setSelectedInstance({
        _id: slot._id,
        ...slot.content
      })

      // Check if slot has a component - if yes, show data editor by default
      const hasComponent = slot.content?.component &&
                          Array.isArray(slot.content?.component) &&
                          slot.content?.component.length > 0

      setUseDataEditor(hasComponent)

      // Check if slot has a component and if there's a custom component editor for it
      const componentInfo = slot.content?.component?.[0]?.content || slot.content?.component?.[0]
      const componentId = componentInfo?.id || componentInfo?.itemLabel

      if (componentId) {
        const Editor = ComponentEditorRegistry.getEditor(componentId)
        setCustomEditor(Editor ? { Component: Editor, componentId } : null)
      } else {
        setCustomEditor(null)
      }
    } else {
      // Creating new slot
      setSelectedInstance(null)
      setCustomEditor(null)
      setUseDataEditor(false)
    }
  }, [slot, isOpen])

  const handleSave = async (fieldValues) => {
    // Check if a slot with this id already exists (only for new slots)
    if (!slot?._id && fieldValues.id) {
      const existingResponse = await GetCMSContent({
        match: {
          'content.type': 'AldooLayoutSlot',
          'content.id': fieldValues.id.trim()
        },
        itemsPerPage: 1
      })

      if (existingResponse.result && existingResponse.data && existingResponse.data.length > 0) {
        showNotification({
          message: `A layout slot with ID "${fieldValues.id}" already exists`,
          className: 'bg-admin_error text-white'
        })
        return null
      }
    }

    // ContentEditor passes the field values, we need to wrap them in content object
    let contentData = {
      ...fieldValues,
      type: 'AldooLayoutSlot'
    }

    // Apply pre-save formatter to normalize data
    const preFormatter = getPreSaveFormatter('AldooLayoutSlot')
    if (preFormatter) {
      contentData = preFormatter(contentData)
    }

    const savePayload = {
      content: contentData
    }

    // If editing, include the _id at root level
    if (slot?._id) {
      savePayload._id = slot._id
    }

    const response = await SaveCMSContent({ payload: savePayload })

    if (response.result) {
      showNotification({
        message: `Layout slot "${fieldValues.id}" ${slot ? 'updated' : 'created'} successfully`,
        className: 'bg-admin_primary text-admin_text_inv'
      })

      // Call onSave callback if provided
      if (onSaveCallback) {
        onSaveCallback(response)
      }

      onClose(response)
      return response
    } else {
      showNotification({
        message: response.error || 'Failed to save layout slot',
        className: 'bg-admin_error text-white'
      })
      return null
    }
  }

  const handleClose = () => {
    setSelectedInstance(null)
    onClose()
  }

  // Handle custom editor save
  const handleCustomEditorSave = async (updatedSlot) => {
    // Save the slot through the normal flow
    const fieldValues = updatedSlot.content

    // ContentEditor passes the field values, we need to wrap them in content object
    let contentData = {
      ...fieldValues,
      type: 'AldooLayoutSlot'
    }

    // Apply pre-save formatter to normalize data
    const preFormatter = getPreSaveFormatter('AldooLayoutSlot')
    if (preFormatter) {
      contentData = preFormatter(contentData)
    }

    const savePayload = {
      content: contentData
    }

    // If editing, include the _id at root level
    if (slot?._id) {
      savePayload._id = slot._id
    }

    const response = await SaveCMSContent({ payload: savePayload })

    if (response.result) {
      showNotification({
        message: `Layout slot "${fieldValues.id}" updated successfully`,
        className: 'bg-admin_primary text-admin_text_inv'
      })

      // Call onSave callback if provided
      if (onSaveCallback) {
        onSaveCallback(response)
      }

      handleClose()
      return response
    } else {
      showNotification({
        message: response.error || 'Failed to save layout slot',
        className: 'bg-admin_error text-white'
      })
      return null
    }
  }

  if (!isOpen) return null

  // If we have a custom component editor, render it instead
  if (customEditor) {
    const { Component: CustomEditor, componentId } = customEditor

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={componentId}
        variant="admin"
        className="w-full md:w-[80%] xl:w-[900px]"
        headerContent={headerButtons}
      >
        <CustomEditor
          slot={slot}
          onSave={handleCustomEditorSave}
          onClose={handleClose}
          onHeaderButtons={setHeaderButtons}
        />
      </Modal>
    )
  }

  // If slot has a component, show the data editor
  if (useDataEditor && slot) {
    // Get component name for the modal title
    const componentInfo = slot.content?.component?.[0]?.content || slot.content?.component?.[0]
    const componentName = componentInfo?.name || componentInfo?.itemLabel || 'Component'

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={componentName}
        variant="admin"
        className="w-full md:w-[80%] xl:w-[900px]"
      >
        <SlotDataEditor
          slot={slot}
          onSave={handleCustomEditorSave}
          onClose={handleClose}
        />
      </Modal>
    )
  }

  // Default: render standard ContentEditor (for creating new slots or editing slot structure)
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={slot ? 'Edit Layout Slot' : 'Create Layout Slot'}
      variant="admin"
      className="w-full md:w-[90%] xl:w-[1200px]"
    >
      <ContentEditor
        contentType={AldooLayoutSlotType}
        fieldValues={selectedInstance || {}}
        onSave={handleSave}
        onClickSave={(result) => {
          // Only close if save was successful
          if (!result) return
          handleClose()
        }}
        onCancel={handleClose}
      />
    </Modal>
  )
}

export default AldooLayoutSlotDialog